import { useCallback, useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { Role } from "@/briefpoint-client";
import AddEditClientModal from "@/components/CaseManagement/AddEditClientModal";
import ClientTable from "@/components/CaseManagement/ClientTable";
import Loading from "@/components/Loading";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useClientApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useFirm from "@/hooks/useFirm";
import "./styles.scss";
import PageHeader from "@/components/PageHeader";
import type { ClientResponse, UserResponse } from "@/lib/types";

export default function ClientsMulti() {
  const { user, firm } = useAuth();
  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");
  const clientApi = useClientApi();
  const [showAddClient, setShowAddClient] = useState(false);
  const [clients, setClients] = useState<ClientResponse[]>();
  const [search, setSearch] = useState("");
  const [users, , , ,] = useFirm(firm?.id, false);

  const loadClients = useCallback(
    async (user: UserResponse) => {
      if (!user.firmId) throw new Error("User firmId is undefined");
      const clients = (await clientApi.clientGetAll({
        firmId: user.firmId,
        usersClientsOnly: !user.role?.includes(Role.FirmAdmin),
      })) as ClientResponse[];
      setClients(clients);
    },
    [clientApi],
  );

  useEffect(() => {
    loadClients(user);
  }, [user, loadClients]);

  const filteredClients = clients?.filter((x) => {
    if (!search) return true;

    const name = x.name?.toLocaleLowerCase() ?? "";
    if (name.includes(search.toLowerCase())) {
      return true;
    }

    return false;
  });

  return (
    <Container className="page-container-multi">
      <Loading isLoading={clients === undefined} className="mt-3">
        <PageHeader
          testId="clients-header"
          title="Clients"
          cta={
            <Button variant="primary" onClick={() => setShowAddClient(true)}>
              Add New Client
            </Button>
          }
        />
        <div className="search-input-wrapper">
          <SearchInput
            value={search}
            placeholder="Search clients"
            onChange={(value) => setSearch(value.toLowerCase())}
            className="mb-2"
          />
        </div>
        <AddEditClientModal
          title="Add Client"
          submitButtonText="Add"
          isShow={showAddClient}
          onClose={() => setShowAddClient(false)}
          onClientSaved={(client) => {
            if (clients) {
              setClients((c) => [...(c ?? []), client]);
            } else {
              setClients([client]);
            }
          }}
          users={users}
        />
        {(filteredClients?.length ?? 0) > 5 && (
          <div>
            <h2 style={{ marginTop: "0px" }}>Recent Clients</h2>
            <ClientTable
              clients={filteredClients
                ?.slice()
                .sort((a, b) => {
                  if (a.lastActivity && b.lastActivity) {
                    return a.lastActivity > b.lastActivity ? -1 : 1;
                  }
                  if (a.lastActivity) {
                    return -1;
                  }
                  if (b.lastActivity) {
                    return 1;
                  }
                  return 0;
                })
                .slice(0, 5)}
              addClient={() => setShowAddClient(true)}
              showIfEmpty={false}
            />
            <span>&nbsp;&nbsp;</span>
            <h2 style={{ marginTop: "0px" }}>All Clients</h2>
          </div>
        )}
        {(filteredClients?.length ?? 0) <= 0 && !!search?.length ? (
          <div>No results found.</div>
        ) : (
          <ClientTable
            clients={filteredClients?.slice().sort((a, b) => {
              if (a.name && b.name) {
                return a.name.localeCompare(b.name);
              }
              if (a.name) {
                return -1;
              }
              if (b.name) {
                return 1;
              }
              return 0;
            })}
            addClient={() => setShowAddClient(true)}
            showIfEmpty={true}
          />
        )}
      </Loading>
    </Container>
  );
}
